import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useSnackbar } from "notistack";
import { makeStyles } from "@material-ui/core/styles";
import AppLoader from "components/AppLoader/Index";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import {
  filterNachaProcessing,
  generateNachaFiles,
  getMerchantNachaBusinesses,
  getNachaMerchantCodes,
  saveNachaMerchantDetails,
} from "../../../actions/Operations";
import NachaMerchantTable from "../../UserProfile/NachaMerchantTable";
import RunNachaCard from "./components/RunNachaCard";
import ProcessingResultTable from "./components/ProcessingResultTable";
import {
  createFailureResult,
  createNachaProcessingPayload,
  normalizeNachaResponse,
} from "./services/nachaService";

const useStyles = makeStyles(theme => ({
  page: {
    width: "100%",
    paddingBottom: theme.spacing(3),
  },
  tabs: {
    marginBottom: theme.spacing(3),
  },
  tabPanel: {
    width: "100%",
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: "6px",
  },
  field: {
    minWidth: "320px",
    marginTop: theme.spacing(2),
  },
  helperText: {
    marginTop: theme.spacing(2),
  },
  optionItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(1, 2),
    width: "100%",
  },
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  buttonProgress: {
    marginRight: theme.spacing(1),
  },
}));

const getRowsFromNachaResponse = response => {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.data?.items)) {
    return response.data.items;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

const normalizeBusinessOptions = response => {
  const items = response?.data?.items || response?.items || [];

  return items
    .filter(item => item && (item.merchantId || item.businessName || item.emailId))
    .map(item => ({
      merchantId: item.merchantId || "",
      emailId: item.emailId || "",
      businessName: item.businessName || "",
    }));
};

function NACHAProcessing({
  filterNachaProcessing,
  generateNachaFiles: generateFiles,
  getMerchantNachaBusinesses: loadBusinessOptions,
  getNachaMerchantCodes: loadMerchantCodes,
  saveNachaMerchantDetails: saveMerchantDetails,
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [processingLoading, setProcessingLoading] = React.useState(false);
  const [processingResult, setProcessingResult] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(0);
  const [businessOptions, setBusinessOptions] = React.useState([]);
  const [selectedBusiness, setSelectedBusiness] = React.useState("");
  const [selectedBusinessEmail, setSelectedBusinessEmail] = React.useState("");
  const [merchantRows, setMerchantRows] = React.useState([]);
  const [merchantCodeOptions, setMerchantCodeOptions] = React.useState([]);
  const [merchantCodesFallbacked, setMerchantCodesFallbacked] = React.useState(false);
  const [businessLoading, setBusinessLoading] = React.useState(false);
  const [businessLoaded, setBusinessLoaded] = React.useState(false);
  const [merchantLoading, setMerchantLoading] = React.useState(false);
  const [merchantSaving, setMerchantSaving] = React.useState(false);
  const [merchantMessage, setMerchantMessage] = React.useState("");
  const [filteringLoading, setFilteringLoading] = React.useState(false);

  const handleFilterResults = React.useCallback(
    payload => {
      if (filteringLoading) {
        return;
      }

      setFilteringLoading(true);
      filterNachaProcessing(
        payload,
        response => {
          const nextResult = normalizeNachaResponse(response);
          setProcessingResult(nextResult);
          // If backend indicates a failure via payload, surface toast and ensure table is cleared
          if (nextResult && (nextResult.status === "failure" || nextResult.variant === "error")) {
            enqueueSnackbar(nextResult.message || "Unable to apply NACHA filters.", { variant: "error" });
          }
          setFilteringLoading(false);
        },
        error => {
          setFilteringLoading(false);
          // Normalize any payload present in the error to create a consistent failure result
          try {
            const failureResult = normalizeNachaResponse(error);
            setProcessingResult(failureResult);
            enqueueSnackbar(
              error?.message || failureResult?.message || error?.data?.message || "Unable to apply NACHA filters.",
              { variant: "error" }
            );
          } catch (e) {
            // Fallback: ensure UI is cleared if normalization fails
            const failureResult = createFailureResult();
            setProcessingResult(failureResult);
            enqueueSnackbar(error?.message || error?.data?.message || "Unable to apply NACHA filters.", { variant: "error" });
          }
        }
      );
    },
    [enqueueSnackbar, filteringLoading, filterNachaProcessing]
  );

  const handleRunNachaProcessing = React.useCallback(() => {
    if (processingLoading) {
      return;
    }

    setProcessingLoading(true);

    generateFiles(
      createNachaProcessingPayload(),
      response => {
        const nextResult = normalizeNachaResponse(response);
        setProcessingResult(nextResult);
        enqueueSnackbar(nextResult.message, { variant: nextResult.variant });
        setProcessingLoading(false);
      },
      () => {
        const failureResult = createFailureResult();
        setProcessingResult(failureResult);
        enqueueSnackbar(failureResult.message, { variant: "error" });
        setProcessingLoading(false);
      }
    );
  }, [enqueueSnackbar, generateFiles, processingLoading]);

  React.useEffect(() => {
    let isMounted = true;

    loadMerchantCodes(
      "",
      "",
      response => {
        if (!isMounted) {
          return;
        }

        const nextResult = normalizeNachaResponse(response);
        const nextOptions = Array.from(
          new Set(nextResult.details.map(row => row.merchantCode).filter(Boolean))
        );
        setMerchantCodeOptions(nextOptions);
      },
      () => {
        if (!isMounted) {
          return;
        }
        setMerchantCodeOptions([]);
      }
    );

    return () => {
      isMounted = false;
    };
  }, [loadMerchantCodes]);

  React.useEffect(() => {
    let isMounted = true;

    if (
      merchantCodeOptions.length === 0 &&
      businessOptions.length > 0 &&
      !merchantCodesFallbacked
    ) {
      const firstBusiness = businessOptions[0];
      const email = firstBusiness?.emailId || "";
      const businessId = firstBusiness?.merchantId || "";

      if (email || businessId) {
        loadMerchantCodes(
          email,
          businessId,
          response => {
            if (!isMounted) {
              return;
            }

            const nextResult = normalizeNachaResponse(response);
            const nextOptions = Array.from(
              new Set(nextResult.details.map(row => row.merchantCode).filter(Boolean))
            );
            setMerchantCodeOptions(nextOptions);
            setMerchantCodesFallbacked(true);
          },
          () => {
            if (!isMounted) {
              return;
            }
            setMerchantCodeOptions([]);
            setMerchantCodesFallbacked(true);
          }
        );
      }
    }

    return () => {
      isMounted = false;
    };
  }, [businessOptions, loadMerchantCodes, merchantCodeOptions.length, merchantCodesFallbacked]);

  React.useEffect(() => {

    setBusinessLoading(true);
    setMerchantMessage("");
    loadBusinessOptions(
      response => {
        const nextOptions = normalizeBusinessOptions(response);
        setBusinessOptions(nextOptions);
        setBusinessLoading(false);
        setBusinessLoaded(true);
        if (nextOptions.length === 0) {
          setSelectedBusiness("");
          setMerchantRows([]);
          setMerchantMessage("No merchant configuration found.");
        }
      },
      error => {
        setBusinessLoading(false);
        setBusinessLoaded(true);
        setBusinessOptions([]);
        setSelectedBusiness("");
        setMerchantRows([]);
        setMerchantMessage(error?.message || error?.data?.message || "Unable to load businesses.");
      }
    );
  }, [activeTab, loadBusinessOptions]);

  const handleBusinessChange = (event, selectedOption) => {
    const nextBusinessId = selectedOption?.merchantId || "";

    setSelectedBusiness(nextBusinessId);
    setSelectedBusinessEmail(selectedOption?.emailId || "");
    setMerchantRows([]);
    setMerchantMessage("");

    if (!nextBusinessId || !selectedOption?.emailId) {
      setMerchantMessage("No merchant configuration found.");
      return;
    }

    setMerchantLoading(true);
    loadMerchantCodes(
      selectedOption.emailId,
      nextBusinessId,
      response => {
        const nextRows = getRowsFromNachaResponse(response);
        setMerchantLoading(false);
        setMerchantRows(nextRows);
        setMerchantMessage(nextRows.length === 0 ? "No merchant configuration found." : "");
      },
      error => {
        setMerchantLoading(false);
        setMerchantRows([]);
        setMerchantMessage(
          error?.message || error?.data?.message || "Unable to load merchant configuration."
        );
      }
    );

  };

  const normalizeNachaRow = (row, defaultMerchantId = "") => ({
    achCodeId: row.achCodeId || row.ach_code_id || row.ACHCodeId || row.achCode || "",
    apiMerchantCode: row.apiMerchantCode || row.api_merchant_code || row.APIMerchantCode || "",
    nachaMerchantCode: row.nachaMerchantCode || row.nacha_merchant_code || row.NACHAMerchantCode || "",
    accountType: row.accountType || "",
    merchantName: row.merchantName || row.merchant_name || row.MerchantName || "",
    merchantTollNumber:
      row.merchantTollNumber ||
      row.merchant_toll_number ||
      row.TollNumber ||
      row.tollNumber ||
      "",
    merchantIdentificationNumber:
      row.merchantIdentificationNumber ||
      row.merchant_identification_number ||
      row.MerchantIdentificationNumber ||
      row.merchantIdNumber ||
      row.merchantId ||
      "",
    merchantTrustAccountNumber:
      row.merchantTrustAccountNumber ||
      row.merchant_trust_account_number ||
      row.MerchantTrustAccountNumber ||
      row.trustAccountNumber ||
      row.trustAccountNo ||
      "",
    sftpFileLocation:
      row.sftpFileLocation || row.sftp_file_location || row.SFTPFileLocation || "",
    merchantId: row.merchantId || row.merchant_id || row.MerchantId || defaultMerchantId,
  });

  const refreshMerchantConfiguration = (email) => {
    if (!email) {
      return;
    }

    setMerchantLoading(true);
    setMerchantMessage("");

    loadMerchantCodes(
      email,
      selectedBusiness,
      (response) => {
        const nextRows = getRowsFromNachaResponse(response);
        setMerchantRows(nextRows);
        setMerchantLoading(false);
        setMerchantMessage(nextRows.length === 0 ? "No merchant configuration found." : "");
      },
      (error) => {
        setMerchantLoading(false);
        setMerchantRows([]);
        setMerchantMessage(
          error?.message || error?.data?.message || "Unable to load merchant configuration."
        );
      }
    );
  };

  const handleMerchantSave = rows => {
    if (!selectedBusiness) {
      return;
    }
    // save proceeds without global SFTP validation; per-row validation runs on blur

    const payload = {
      merchantId: selectedBusiness,
      nachaInfoArr: rows.map(row => normalizeNachaRow(row, selectedBusiness)),
    };

    setMerchantSaving(true);
    saveMerchantDetails(
      payload,
      () => {
        setMerchantSaving(false);
        enqueueSnackbar("NACHA Details saved successfully.", { variant: "success" });
        refreshMerchantConfiguration(selectedBusinessEmail);
      },
      (error) => {
        setMerchantSaving(false);
        const message =
          error?.message || error?.data?.message || "Unable to save merchant configuration.";
        enqueueSnackbar(message, { variant: "error" });
      }
    );
  };

  return (
    <div className={classes.page}>
      <AppLoader loader={processingLoading} />
      <Tabs
        className={classes.tabs}
        indicatorColor="primary"
        onChange={(event, value) => setActiveTab(value)}
        textColor="primary"
        value={activeTab}
      >
        <Tab label="Processing" />
        <Tab label="Merchant Configuration" />
      </Tabs>

      {activeTab === 0 && (
        <div className={classes.tabPanel}>
          <RunNachaCard loading={processingLoading} onRun={handleRunNachaProcessing} />
          <ProcessingResultTable
            details={processingResult?.details || []}
            summary={processingResult?.summary}
            pagination={processingResult?.pagination}
            merchantOptions={merchantCodeOptions}
            onFilter={handleFilterResults}
          />
        </div>
      )}

      {activeTab === 1 && (
        <div className={classes.tabPanel}>
          <Paper className={classes.card}>
            <Typography gutterBottom variant="h5">
              Merchant Configuration
            </Typography>
            <Autocomplete
              className={classes.field}
              style={{ width: "26%" }}
              disableClearable={false}
              getOptionLabel={option => option?.businessName || option?.emailId || ""}
              filterOptions={(options, { inputValue }) => {
                const searchText = inputValue.trim().toLowerCase();
                return options.filter(option => {
                  const businessName = option.businessName ? option.businessName.toLowerCase() : "";
                  const emailId = option.emailId ? option.emailId.toLowerCase() : "";
                  return (
                    businessName.includes(searchText) ||
                    emailId.includes(searchText)
                  );
                });
              }}
              loading={businessLoading}
              noOptionsText="No businesses found"
              onChange={handleBusinessChange}
              options={businessOptions}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Business"
                  placeholder="Search business or email"
                  variant="outlined"
                />
              )}
              renderOption={option => (
                <Box className={classes.optionItem} component="li">
                  <Typography variant="body2">{option.businessName}</Typography>
                  {option.emailId && (
                    <Typography color="textSecondary" variant="caption">
                      {option.emailId}
                    </Typography>
                  )}
                </Box>
              )}
              value={
                businessOptions.find(item => item.merchantId === selectedBusiness) || null
              }
            />

            {businessLoading && (
              <Typography className={classes.helperText} color="textSecondary">
                Loading businesses...
              </Typography>
            )}

            {!businessLoading && merchantMessage && (
              <Typography className={classes.helperText} color="textSecondary">
                {merchantMessage}
              </Typography>
            )}

            {merchantLoading && (
              <Typography className={classes.helperText} color="textSecondary">
                Loading merchant configuration...
              </Typography>
            )}

            {merchantRows.length > 0 && (
              <Box mt={3}>
                <NachaMerchantTable
                  loading={merchantLoading}
                  message={merchantMessage}
                  onSave={handleMerchantSave}
                  rows={merchantRows}
                  saving={merchantSaving}
                  saveLabel="Save Merchant Configuration"
                  title="Merchant Configuration"
                  merchantId={selectedBusiness}
                />
              </Box>
            )}
          </Paper>
        </div>
      )}
    </div>
  );
}

NACHAProcessing.propTypes = {
  filterNachaProcessing: PropTypes.func,
  generateNachaFiles: PropTypes.func.isRequired,
  getMerchantNachaBusinesses: PropTypes.func.isRequired,
  getNachaMerchantCodes: PropTypes.func.isRequired,
  saveNachaMerchantDetails: PropTypes.func.isRequired,
};

export default connect(null, {
  filterNachaProcessing,
  generateNachaFiles,
  getMerchantNachaBusinesses,
  getNachaMerchantCodes,
  saveNachaMerchantDetails,
})(NACHAProcessing);
